import {
	formatNominalFrequency,
	Fraction,
	freqFromBandIndex,
	roundFrequency,
} from "@soft-db/data-formatter";
import { FieldFactory } from "@soft-db/form";
import { Match, Switch } from "@soft-db/soft-utils";
import { FormField, NumberInput, SoftSelect } from "@soft-db/ui";
import { defineComponent } from "vue";
import { QueryDataConfigForm } from "./QueryDataConfig";

type FrequencyItem = {
	label: string;
	value: number;
};

export default defineComponent({
	name: "FrequencyInput",
	props: {
		useField: {
			type: Function as PropType<FieldFactory<QueryDataConfigForm>>,
			required: true,
		},
	},
	setup(props) {
		const { t } = useI18n();

		const freq = props.useField("$dataConfig.frequency", {
			isNumber: true,
			isRequired: true,
			min: 1,
			max: 20_000,
		});

		const spectrum = props.useField("$dataConfig.spectrum");

		const fractionType = computed<Fraction | null>(() => {
			switch (spectrum.data.value) {
				case "1":
					return 1;
				case "3":
					return 3;
			}

			return null;
		});

		const possibleFrequencies = computed<FrequencyItem[]>(() => {
			if (fractionType.value == null) {
				return [];
			}

			let start: number;
			let end: number;

			if (fractionType.value === 3) {
				start = -30;
				end = 13;
			} else {
				start = -10;
				end = 4;
			}

			const freqs: FrequencyItem[] = [];
			for (let i = start; i <= end; i++) {
				const value = freqFromBandIndex(i, fractionType.value).nominal;

				freqs.push({
					value,
					label: formatNominalFrequency(value),
				});
			}

			return freqs;
		});

		function roundFreqValue() {
			if (fractionType.value == null || freq.data.value == null) {
				return;
			}

			freq.data.value = roundFrequency(freq.data.value, fractionType.value);
		}

		watch(spectrum.data, roundFreqValue);

		return () => (
			<Switch>
				<Match when={fractionType.value != null}>
					<SoftSelect
						label={t("dashboard.frequency")}
						item-text="label"
						item-value="value"
						items={possibleFrequencies.value}
						{...freq.attrs}
					/>
				</Match>
				<Match default>
					<FormField label={t("dashboard.frequency")} errors={freq.errors.value}>
						<NumberInput v-model={freq.data.value} />
					</FormField>
				</Match>
			</Switch>
		);
	},
});
